import { useMemo, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';

/** Um bloco da árvore. `extra` é livre para o consumidor renderizar o que tiver. */
export interface TreeBlock<TExtra = unknown> {
    country: string;
    post: string;
    block: string;
    steps: number[];
    impressions?: number | null;
    extra?: TExtra;
}

interface PostNode<TExtra> {
    post: string;
    blocks: TreeBlock<TExtra>[];
    impressions: number;
}

interface CountryNode<TExtra> {
    country: string;
    posts: PostNode<TExtra>[];
    blockCount: number;
    skippedCount: number;
}

/** domínio → país → id_post → blocos */
function buildTree<TExtra>(items: TreeBlock<TExtra>[]): CountryNode<TExtra>[] {
    const byCountry = new Map<string, Map<string, TreeBlock<TExtra>[]>>();

    for (const item of items) {
        if (!byCountry.has(item.country)) byCountry.set(item.country, new Map());
        const posts = byCountry.get(item.country)!;
        if (!posts.has(item.post)) posts.set(item.post, []);
        posts.get(item.post)!.push(item);
    }

    return [...byCountry.entries()]
        .map(([country, posts]) => {
            const postNodes: PostNode<TExtra>[] = [...posts.entries()]
                .map(([post, blocks]) => ({
                    post,
                    blocks: [...blocks].sort((a, b) => a.block.localeCompare(b.block)),
                    impressions: blocks.reduce((sum, b) => sum + (b.impressions ?? 0), 0),
                }))
                .sort(
                    (a, b) =>
                        b.impressions - a.impressions ||
                        b.blocks.length - a.blocks.length ||
                        a.post.localeCompare(b.post),
                );

            const all = postNodes.flatMap((p) => p.blocks);
            return {
                country,
                posts: postNodes,
                blockCount: all.length,
                skippedCount: all.filter((b) => b.steps.length === 0).length,
            };
        })
        .sort((a, b) => b.blockCount - a.blockCount || a.country.localeCompare(b.country));
}

interface GranulationTreeProps<TExtra> {
    items: TreeBlock<TExtra>[];
    /** Quando há busca ativa, tudo abre — esconder resultado anula o filtro. */
    forceExpanded?: boolean;
    /** Termos de segmentação: post do WordPress habilita slug e link. */
    byPost?: boolean;
    unitLabel?: string;
    domainName?: string;
    slugs?: Record<string, string | null>;
    /** Detalhe extra abaixo dos steps de cada bloco. */
    renderBlockDetail?: (item: TreeBlock<TExtra>) => ReactNode;
    /** Linha de métricas ao lado do nome do bloco. */
    renderBlockMeta?: (item: TreeBlock<TExtra>) => ReactNode;
    emptyMessage?: string;
}

export function GranulationTree<TExtra>({
    items,
    forceExpanded = false,
    byPost = true,
    unitLabel = 'post',
    domainName,
    slugs = {},
    renderBlockDetail,
    renderBlockMeta,
    emptyMessage = 'Nenhum bloco para exibir.',
}: GranulationTreeProps<TExtra>) {
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const tree = useMemo(() => buildTree(items), [items]);

    const isOpen = (key: string) => forceExpanded || expanded.has(key);
    const toggle = (key: string) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });

    if (tree.length === 0) {
        return (
            <p className="rounded-lg border-2 border-dashed border-gray-200 py-8 text-center text-sm text-gray-500">
                {emptyMessage}
            </p>
        );
    }

    return (
        <div className="space-y-3">
            {tree.map((country) => (
                <div
                    key={country.country}
                    className="overflow-hidden rounded-xl border border-gray-200 bg-white"
                >
                    <button
                        type="button"
                        onClick={() => toggle(country.country)}
                        className="flex w-full items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-gray-50"
                    >
                        <Chevron open={isOpen(country.country)} />
                        <span className="font-semibold text-gray-900">{country.country}</span>
                        <span className="text-xs text-gray-500">
                            {country.posts.length} {unitLabel}
                            {country.posts.length === 1 ? '' : 's'} · {country.blockCount} bloco
                            {country.blockCount === 1 ? '' : 's'}
                        </span>
                        {country.skippedCount > 0 && (
                            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700">
                                {country.skippedCount} sem granulação
                            </span>
                        )}
                    </button>

                    {isOpen(country.country) && (
                        <div className="space-y-1.5 border-t border-gray-100 bg-gray-50/50 px-3 py-3">
                            {country.posts.map((post) => {
                                const key = `${country.country}/${post.post}`;
                                const slug = slugs[post.post] ?? null;
                                const url = slug
                                    ? `https://${domainName}/${slug}/`
                                    : `https://${domainName}/?p=${post.post}`;

                                return (
                                    <div
                                        key={post.post}
                                        className="overflow-hidden rounded-lg border border-gray-200 bg-white"
                                    >
                                        <div className="flex items-center gap-2 px-3 py-2">
                                            <button
                                                type="button"
                                                onClick={() => toggle(key)}
                                                className="flex min-w-0 flex-1 items-center gap-2 text-left"
                                            >
                                                <Chevron open={isOpen(key)} small />
                                                <span
                                                    className="truncate rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-800"
                                                    title={post.post}
                                                >
                                                    {post.post}
                                                </span>
                                                {slug && (
                                                    <span
                                                        className="truncate text-xs text-gray-600"
                                                        title={slug}
                                                    >
                                                        /{slug}
                                                    </span>
                                                )}
                                            </button>

                                            <span className="shrink-0 text-xs text-gray-500">
                                                {post.blocks.length} bloco
                                                {post.blocks.length === 1 ? '' : 's'}
                                                {post.impressions > 0 &&
                                                    ` · ${post.impressions.toLocaleString('pt-BR')} impressões`}
                                            </span>

                                            {byPost && domainName && (
                                                <a
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title={
                                                        slug
                                                            ? `Abrir /${slug}`
                                                            : `Abrir por id (?p=${post.post})`
                                                    }
                                                    className="shrink-0 rounded p-1 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                                                >
                                                    <ExternalLink className="h-3.5 w-3.5" />
                                                </a>
                                            )}
                                        </div>

                                        {isOpen(key) && (
                                            <div className="divide-y divide-gray-100 border-t border-gray-100">
                                                {post.blocks.map((block, index) => (
                                                    <div
                                                        key={`${block.block}-${index}`}
                                                        className="px-3 py-3"
                                                    >
                                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                                            <span className="text-sm font-medium text-gray-800">
                                                                {block.block}
                                                            </span>
                                                            {block.steps.length > 0 && (
                                                                <span className="text-xs text-gray-500">
                                                                    {block.steps.length} steps ·{' '}
                                                                    {block.steps[0]} →{' '}
                                                                    {block.steps[block.steps.length - 1]}
                                                                </span>
                                                            )}
                                                            {renderBlockMeta?.(block)}
                                                        </div>

                                                        {block.steps.length > 0 && (
                                                            <div className="mt-2 flex flex-wrap gap-1">
                                                                {block.steps.map((step, i) => (
                                                                    <span
                                                                        key={`${step}-${i}`}
                                                                        className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[11px] text-gray-700"
                                                                    >
                                                                        {step}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {renderBlockDetail?.(block)}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function Chevron({ open, small }: { open: boolean; small?: boolean }) {
    const size = small ? 'h-3.5 w-3.5' : 'h-4 w-4';
    return open ? (
        <ChevronDown className={`${size} shrink-0 text-gray-400`} />
    ) : (
        <ChevronRight className={`${size} shrink-0 text-gray-400`} />
    );
}
